import React, { useEffect } from 'react'
import DashbardFoodieStats from '../../components/Dashboard/DashbardFoodiStats'
import DashbardTrackingStats from '../../components/Dashboard/DashboardTrackingStats'
import WidgetWrapper from '../../components/WidgetWrapper'
import CampaignDonationChart from '../../components/Dashboard/CampaignDonationChart'
import DashbardFoodiCountries from '../../components/Dashboard/DashbardFoodiCountries'
import DashboardFunnelWidget from '../../components/Dashboard/DashboardFunnelWidget'
import DashboardCampaignHealthWidget from '../../components/Dashboard/DashboardCampaignHealthWidget'
import DashboardRevenueQualityWidget from '../../components/Dashboard/DashboardRevenueQualityWidget'
import DashboardCohortConversionWidget from '../../components/Dashboard/DashboardCohortConversionWidget'
import DashboardReferralIntelligenceWidget from '../../components/Dashboard/DashboardReferralIntelligenceWidget'
import DashboardGeoPerformanceWidget from '../../components/Dashboard/DashboardGeoPerformanceWidget'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../state/store'
import DashboardFilterBar, { DatePreset, DashboardFiltersState, formatDashboardDate, getPresetRange } from '../../components/Dashboard/DashboardFilterBar'
import { fetchFilteredDashboardStats, getDashboardP1InsightsRequest, getDashboardP2InsightsRequest, getFilteredDashboardStatsRequest } from '../../services/DashboardService'
import { DashboardP1Insights, DashboardP2Insights, FilteredDashboardStats } from '../../types/dashboard.types'

const fmt = formatDashboardDate;

type CompareMode = 'previous_period' | 'preset' | 'custom';

const defaultCompareStats: FilteredDashboardStats = {
  totalBars: 0,
  totalDonationAmount: 0,
  unsyncedTap2FeedDonationAmount: 0,
  tap2FeedBalanceAmount: 0,
  trackYourImpactDonationAmount: 0,
  syncedTap2FeedDonationAmount: 0,
  totalRegistrations: 0,
  inviteFOODIEClicks: 0,
  sharedClicks: 0,
  ordersWaitingFullfillment: 0,
  fulfilledWaitingAssignment: 0,
  campaignBars: [],
  leaderboard: [],
};

export default function Dashboard() {

  const dispatch = useDispatch<AppDispatch>();

  const defaultPreset: DatePreset = 'this_month';
  const defaultRange = getPresetRange(defaultPreset);
  const [filters, setFilters] = React.useState<DashboardFiltersState>({
    fromDate: defaultRange.fromDate,
    toDate: defaultRange.toDate,
    activePreset: defaultPreset,
    selectedCampaignIds: [],
  });

  const [compareEnabled, setCompareEnabled] = React.useState(false);
  const [compareMode, setCompareMode] = React.useState<CompareMode>('previous_period');
  const [comparePreset, setComparePreset] = React.useState<DatePreset>('last_week');
  const [customCompareFromDate, setCustomCompareFromDate] = React.useState(defaultRange.fromDate);
  const [customCompareToDate, setCustomCompareToDate] = React.useState(defaultRange.toDate);
  const [compareStats, setCompareStats] = React.useState<FilteredDashboardStats | null>(null);
  const [compareLoading, setCompareLoading] = React.useState(false);
  const [p1Insights, setP1Insights] = React.useState<DashboardP1Insights | null>(null);
  const [p1InsightsLoading, setP1InsightsLoading] = React.useState(false);
  const [compareP1Insights, setCompareP1Insights] = React.useState<DashboardP1Insights | null>(null);
  const [p2Insights, setP2Insights] = React.useState<DashboardP2Insights | null>(null);
  const [p2InsightsLoading, setP2InsightsLoading] = React.useState(false);
  const [compareP2Insights, setCompareP2Insights] = React.useState<DashboardP2Insights | null>(null);

  useEffect(() => {
    dispatch(fetchFilteredDashboardStats({
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      campaignIds: filters.selectedCampaignIds,
    }));
  }, [dispatch, filters]);

  useEffect(() => {
    const loadP1Insights = async () => {
      setP1InsightsLoading(true);
      try {
        const response = await getDashboardP1InsightsRequest({
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          campaignIds: filters.selectedCampaignIds,
        });
        setP1Insights((response.data || null) as DashboardP1Insights | null);
      } catch (error) {
        setP1Insights(null);
      } finally {
        setP1InsightsLoading(false);
      }
    };

    loadP1Insights();
  }, [filters.fromDate, filters.toDate, filters.selectedCampaignIds]);

  useEffect(() => {
    const loadP2Insights = async () => {
      setP2InsightsLoading(true);
      try {
        const response = await getDashboardP2InsightsRequest({
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          campaignIds: filters.selectedCampaignIds,
        });
        setP2Insights((response.data || null) as DashboardP2Insights | null);
      } catch (error) {
        setP2Insights(null);
      } finally {
        setP2InsightsLoading(false);
      }
    };

    loadP2Insights();
  }, [filters.fromDate, filters.toDate, filters.selectedCampaignIds]);

  const compareRange = React.useMemo(() => {
    if (!compareEnabled) {
      return null as { fromDate: string; toDate: string } | null;
    }

    if (compareMode === 'preset') {
      const range = getPresetRange(comparePreset);
      return { fromDate: range.fromDate, toDate: range.toDate };
    }

    if (compareMode === 'custom') {
      return {
        fromDate: customCompareFromDate,
        toDate: customCompareToDate,
      };
    }

    const currentFrom = new Date(`${filters.fromDate}T00:00:00`);
    const currentTo = new Date(`${filters.toDate}T00:00:00`);
    const dayMs = 24 * 60 * 60 * 1000;
    const spanDays = Math.max(1, Math.round((currentTo.getTime() - currentFrom.getTime()) / dayMs) + 1);

    const prevTo = new Date(currentFrom);
    prevTo.setDate(currentFrom.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevTo.getDate() - spanDays + 1);

    return {
      fromDate: fmt(prevFrom),
      toDate: fmt(prevTo),
    };
  }, [compareEnabled, compareMode, comparePreset, customCompareFromDate, customCompareToDate, filters.fromDate, filters.toDate]);

  useEffect(() => {
    const loadCompareInsights = async () => {
      if (!compareRange) {
        setCompareStats(null);
        setCompareP1Insights(null);
        setCompareP2Insights(null);
        return;
      }

      setCompareLoading(true);
      try {
        const [statsResponse, p1Response, p2Response] = await Promise.all([
          getFilteredDashboardStatsRequest({
            fromDate: compareRange.fromDate,
            toDate: compareRange.toDate,
            campaignIds: filters.selectedCampaignIds,
          }),
          getDashboardP1InsightsRequest({
            fromDate: compareRange.fromDate,
            toDate: compareRange.toDate,
            campaignIds: filters.selectedCampaignIds,
          }),
          getDashboardP2InsightsRequest({
            fromDate: compareRange.fromDate,
            toDate: compareRange.toDate,
            campaignIds: filters.selectedCampaignIds,
          }),
        ]);
        setCompareStats((statsResponse.data || null) as FilteredDashboardStats | null);
        setCompareP1Insights((p1Response.data || null) as DashboardP1Insights | null);
        setCompareP2Insights((p2Response.data || null) as DashboardP2Insights | null);
      } catch (error) {
        setCompareStats(defaultCompareStats);
        setCompareP1Insights(null);
        setCompareP2Insights(null);
      } finally {
        setCompareLoading(false);
      }
    };

    loadCompareInsights();
  }, [compareRange, filters.selectedCampaignIds]);

  const compareLabel = React.useMemo(() => {
    if (!compareEnabled) return '';
    if (compareMode === 'preset') {
      const range = compareRange || getPresetRange(comparePreset);
      const presetTitle = comparePreset
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${presetTitle} (${range.fromDate} → ${range.toDate})`;
    }
    if (compareMode === 'custom') {
      return `${customCompareFromDate} → ${customCompareToDate}`;
    }
    if (compareRange) {
      return `Previous period (${compareRange.fromDate} → ${compareRange.toDate})`;
    }
    return 'previous period';
  }, [compareEnabled, compareMode, comparePreset, customCompareFromDate, customCompareToDate, compareRange]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-4 md:p-6">
      <div className="sticky top-0 z-30 space-y-4 rounded-3xl border border-white/50 dark:border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.9)_0%,rgba(255,255,255,0.82)_100%)] px-4 py-4 shadow-sm backdrop-blur-xl dark:bg-[linear-gradient(180deg,rgba(8,16,13,0.92)_0%,rgba(8,16,13,0.82)_100%)] md:px-6">
        <DashboardFilterBar filters={filters} onChange={setFilters} />

        <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/75 dark:bg-slate-900/60 backdrop-blur-sm px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setCompareEnabled((v) => !v)}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold tracking-wide transition-colors ${compareEnabled ? 'bg-primary-100 text-white dark:bg-primary-70' : 'bg-white/60 dark:bg-slate-800/60 text-gray-700 dark:text-gray-300 border border-white/60 dark:border-white/10'}`}
            >
              {compareEnabled ? 'Comparison enabled' : 'Enable comparison'}
            </button>

            {compareEnabled && (
              <>
                <div className="flex items-center gap-1 rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-800/60 p-1">
                  <button
                    type="button"
                    onClick={() => setCompareMode('previous_period')}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${compareMode === 'previous_period' ? 'bg-primary-100 text-white dark:bg-primary-70' : 'text-gray-600 dark:text-gray-300'}`}
                  >
                    Previous period
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompareMode('preset')}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${compareMode === 'preset' ? 'bg-primary-100 text-white dark:bg-primary-70' : 'text-gray-600 dark:text-gray-300'}`}
                  >
                    Preset
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompareMode('custom')}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${compareMode === 'custom' ? 'bg-primary-100 text-white dark:bg-primary-70' : 'text-gray-600 dark:text-gray-300'}`}
                  >
                    Custom
                  </button>
                </div>

                {compareMode === 'preset' && (
                  <select
                    value={comparePreset}
                    onChange={(e) => setComparePreset(e.target.value as DatePreset)}
                    className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-800/60 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200"
                  >
                    <option value="this_week">This week</option>
                    <option value="last_week">Last week</option>
                    <option value="this_month">This month</option>
                    <option value="last_month">Last month</option>
                    <option value="this_quarter">This quarter</option>
                    <option value="last_quarter">Last quarter</option>
                    <option value="this_year">This year</option>
                    <option value="last_year">Last year</option>
                  </select>
                )}

                {compareMode === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customCompareFromDate}
                      onChange={(e) => setCustomCompareFromDate(e.target.value)}
                      className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-800/60 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">→</span>
                    <input
                      type="date"
                      value={customCompareToDate}
                      onChange={(e) => setCustomCompareToDate(e.target.value)}
                      className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-800/60 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200"
                    />
                  </div>
                )}

                <span className="text-xs text-gray-600 dark:text-gray-300">
                  Comparing against <span className="font-semibold">{compareLabel}</span>{compareLoading ? ' (loading...)' : ''}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto pt-4">
        <div className="flex flex-col gap-4">
          <div className="flex">
            <DashbardFoodieStats
                compareStats={compareStats}
                compareLabel={compareLabel}
                filters={{ fromDate: filters.fromDate, toDate: filters.toDate, campaignIds: filters.selectedCampaignIds }}
              />
          </div>
          <div className="flex">
            <DashbardTrackingStats compareStats={compareStats} compareLabel={compareLabel} />
          </div>

          <div className="w-full px-4 py-3 rounded-2xl border border-white/60 dark:border-white/10 bg-primary-50/85 dark:bg-slate-800/80 backdrop-blur-sm shadow-sm text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            <span>Acquisition & Activation</span>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <DashboardFunnelWidget
              insights={p1Insights}
              compareInsights={compareP1Insights}
              compareLabel={compareLabel}
              loading={p1InsightsLoading}
            />
            <DashboardCohortConversionWidget
              insights={p2Insights}
              compareInsights={compareP2Insights}
              compareLabel={compareLabel}
              loading={p2InsightsLoading}
            />
          </div>

          <div className="w-full px-4 py-3 rounded-2xl border border-white/60 dark:border-white/10 bg-primary-50/85 dark:bg-slate-800/80 backdrop-blur-sm shadow-sm text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            <span>Campaign Performance</span>
          </div>

          <div className="flex flex-col gap-4 w-full">
            <DashboardCampaignHealthWidget
              insights={p1Insights}
              compareInsights={compareP1Insights}
              compareLabel={compareLabel}
              loading={p1InsightsLoading}
            />
            <WidgetWrapper
              title="Campaign Donation Mix"
              titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
              className={"h-full w-full p-4 md:p-5 bg-white/85 dark:bg-slate-900/70 border-white/60 dark:border-white/10"}
            >
              <CampaignDonationChart />
            </WidgetWrapper>
          </div>

          <div className="grid grid-cols-1 gap-4 w-full">
            <DashboardRevenueQualityWidget
              insights={p1Insights}
              compareInsights={compareP1Insights}
              compareLabel={compareLabel}
              loading={p1InsightsLoading}
            />
          </div>

          <div className="w-full px-4 py-3 rounded-2xl border border-white/60 dark:border-white/10 bg-primary-50/85 dark:bg-slate-800/80 backdrop-blur-sm shadow-sm text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            <span>Referral & Geo Performance</span>
          </div>

          <div className="grid grid-cols-1 gap-4 w-full">
            <DashboardReferralIntelligenceWidget
              insights={p2Insights}
              compareInsights={compareP2Insights}
              compareLabel={compareLabel}
              loading={p2InsightsLoading}
              fromDate={filters.fromDate}
              toDate={filters.toDate}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 w-full">
            <DashboardGeoPerformanceWidget
              insights={p2Insights}
              compareInsights={compareP2Insights}
              compareLabel={compareLabel}
              loading={p2InsightsLoading}
            />
          </div>

          <div className="flex flex-col gap-3 h-full w-full justify-between">
            <div className="w-full px-4 py-3 rounded-2xl border border-white/60 dark:border-white/10 bg-primary-50/85 dark:bg-slate-800/80 backdrop-blur-sm shadow-sm text-xl md:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              <span>Global Leaderboard</span>
            </div>
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full min-w-0 min-h-[30rem]">
                  <WidgetWrapper
                    title=""
                    titleStyle="hidden"
                    className={"h-full p-4 md:p-5 bg-gradient-to-br from-emerald-50/70 via-white/75 to-teal-100/70 dark:from-emerald-900/20 dark:via-slate-900/78 dark:to-teal-900/18 border-emerald-200/50 dark:border-emerald-400/15"}
                  >
                    <DashbardFoodiCountries />
                  </WidgetWrapper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
